import React,{useState,useEffect,useRef,useCallback} from 'react';
import Icon from '../Text/Icon';
import classNames from 'classnames';
import {SLASH_COMMANDS} from './CommandPalette';

const allCommands=Object.values(SLASH_COMMANDS);

export default function CommandModal({isOpen,onClose,onSelect}) {
    const [query,setQuery]=useState('');
    const [selectedIndex,setSelectedIndex]=useState(0);
    const inputRef=useRef(null);

    const filtered=allCommands.filter(cmd =>
        cmd.name.toLowerCase().includes(query.toLowerCase())||
        cmd.description.toLowerCase().includes(query.toLowerCase())
    );

    useEffect(() => {
        if(isOpen) {
            setQuery('');
            setSelectedIndex(0);
            setTimeout(() => inputRef.current?.focus(),50);
        }
    },[isOpen]);

    useEffect(() => {
        setSelectedIndex(0);
    },[query]);

    const handleSelect=useCallback((cmd) => {
        onSelect(cmd.name+' ');
        onClose();
    },[onSelect,onClose]);

    const handleKeyDown=useCallback((e) => {
        if(e.key==='ArrowDown') {
            e.preventDefault();
            setSelectedIndex(i => Math.min(i+1,filtered.length-1));
        } else if(e.key==='ArrowUp') {
            e.preventDefault();
            setSelectedIndex(i => Math.max(i-1,0));
        } else if(e.key==='Enter') {
            e.preventDefault();
            if(filtered[selectedIndex]) handleSelect(filtered[selectedIndex]);
        } else if(e.key==='Escape') {
            e.preventDefault();
            onClose();
        }
    },[filtered,selectedIndex,handleSelect,onClose]);

    if(!isOpen) return null;

    const isMac=navigator.userAgent.toUpperCase().includes('MAC');

    return (
        <div className="fixed inset-0 z-[100] flex items-start justify-center pt-[20vh]" onClick={onClose}>
            <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" />
            <div
                className="relative w-full max-w-md bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden"
                onClick={e => e.stopPropagation()}
            >
                {/* Search input */}
                <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-100">
                    <Icon icon="search" className="text-gray-400 text-lg" />
                    <input
                        ref={inputRef}
                        type="text"
                        value={query}
                        onChange={e => setQuery(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder="Type a command..."
                        className="flex-1 text-sm outline-none bg-transparent placeholder-gray-400"
                        autoComplete="off"
                    />
                    <kbd className="px-1.5 py-0.5 text-[10px] font-semibold bg-gray-100 rounded border border-gray-200 text-gray-400">ESC</kbd>
                </div>

                {/* Command list */}
                <div className="max-h-[300px] overflow-y-auto py-1">
                    {filtered.length===0? (
                        <div className="px-4 py-6 text-center text-sm text-gray-400">No matching commands</div>
                    ):(
                        filtered.map((cmd,i) => {
                            const displayShortcut=isMac? cmd.shortcut:cmd.shortcut.replace('⌘','Ctrl+');
                            return (
                                <div
                                    key={cmd.name}
                                    onClick={() => handleSelect(cmd)}
                                    className={classNames(
                                        'flex items-center gap-3 px-4 py-2.5 cursor-pointer transition-colors',
                                        i===selectedIndex? 'bg-primary-50':'hover:bg-gray-50'
                                    )}
                                >
                                    <div className={classNames('flex items-center justify-center w-8 h-8 rounded-lg',cmd.bgColor)}>
                                        <Icon icon={cmd.icon} className={classNames('text-sm',cmd.color)} />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="font-semibold text-sm text-gray-800">{cmd.name}</div>
                                        <div className="text-xs text-gray-500 truncate">{cmd.description}</div>
                                    </div>
                                    <kbd className="px-2 py-1 text-xs font-semibold bg-gray-100 rounded border border-gray-200 text-gray-400">{displayShortcut}</kbd>
                                </div>
                            );
                        })
                    )}
                </div>

                {/* Footer hint */}
                <div className="px-4 py-2 border-t border-gray-100 flex items-center gap-4 text-[10px] text-gray-400">
                    <span><kbd className="font-semibold">↑↓</kbd> navigate</span>
                    <span><kbd className="font-semibold">↵</kbd> select</span>
                    <span><kbd className="font-semibold">esc</kbd> close</span>
                </div>
            </div>
        </div>
    );
}
